/* eslint-disable jsx-a11y/role-supports-aria-props */
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import useStore from '@/store/useStore';
import useMobileNavBehavior from '@/hooks/useMobileNavBehavior';
import { trackEvent, handleGaClick, trackEventDirect, trackEventWithObject } from '@/utils/gaDataPush';
import { getDataAttributes } from '@/utils/dataAttributes';
import { MobileHeaderItem, MobileNavLinkHeaderProp, MobileNavLinksProps } from '@/store/types/IBuilder';
import useMediaQuery from '@/hooks/useMediaQuery';
import LocalCountries from './LocalCountries';
import GbleCountries from './GbleCountries';

export type MobileNavigationProps = {
  isOpen: boolean;
  mobileNavLinks?: unknown[];
};
export function MainMenu({ injectGlbeCountry }) {
  const cart = useStore(state => state.cart);
  const wishlist = useStore(state => state.wishlist);
  const { store, showroomName } = useStore(state => state.showroom);
  const { isAuthenticated, globalEFlag } = useStore(state => state.user);
  const { localGeToggle } = useStore(state => state.glLocation);
  const setIsShowSignInPopUp = useStore(state => state.setIsShowSignInPopUp);

  function setCountryElement() {
    // Still loading
    if (localGeToggle === null || globalEFlag === null) {
      return null;
    }

    if (localGeToggle) {
      return <LocalCountries isMobile />;
    }

    // GlobalE will inject CTA button inside this element.
    return (
      <GbleCountries
        injectGlbeCountry={injectGlbeCountry}
        className="tw-pt-[10px] tw-pl-3 [&_a]:tw-inline-block [&_a]:tw-p-0 [&_a]:tw-pl-1 [&_a]:tw-leading-5 "
      />
    );
  }

  const onSignInClick = event => {
    event.preventDefault();
    setIsShowSignInPopUp(true);
    trackEventDirect('Top Navigation', 'Click', 'Sign In');
    trackEventWithObject({
      event: 'top_navigation',
      general_event_category: 'Sign In',
      general_event_section: 'Undefined',
      general_event_details: 'Undefined',
    });
  };

  return (
    <>
      {store?.location_name !== 'Other' ? (
        <li className="has-ico tw-mt-[10px]">
          <a
            href={store?.location_page}
            data-mobile-access="true"
            data-toggle="modal"
            className="alert-link td-u"
            onClick={() => trackEventDirect('Top Navigation', 'Click', `Visit ${showroomName}`)}
            data-event1-category="Top Navigation"
            data-event1-action="Click"
            data-event1-label={`Visit ${showroomName}`}
            data-noninteract="false"
            data-acsb-clickable="true"
            data-acsb-navigable="true"
            tabIndex={-1}
            data-acsb-now-navigable="false"
            data-acsb-textual-ops="menu"
            data-acsb-hover="true"
          >
            <i
              className="iconfont iconfont-location"
              aria-hidden="true"
              data-acsb-hidden="true"
              data-acsb-force-hidden="true"
            />
            {`Visit ${showroomName}`}
          </a>
        </li>
      ) : (
        <li className="has-ico tw-mt-[10px]">
          <a
            href="/stores/virtual-appointment/"
            aria-label="Virtual Appointment"
            data-event1-category="Top Navigation"
            data-event1-action="Click"
            data-event1-label="Virtual Appointment"
            data-noninteract="false"
            onClick={() => trackEventDirect('Top Navigation', 'Click', 'Virtual Appointment')}
          >
            <i
              className="iconfont iconfont-tv"
              aria-hidden="true"
              data-acsb-hidden="true"
              data-acsb-force-hidden="true"
            />
            <span>Virtual Appointment</span>
          </a>
        </li>
      )}

      <li className="has-ico" data-acsb-menu="li" data-acsb-menu-root="true">
        <a
          href="/contact/"
          aria-label="Contact us"
          data-event1-category="Top Navigation"
          data-event1-action="Click"
          data-event1-label="Contact Us"
          data-noninteract="false"
          data-acsb-overflower="true"
          data-acsb-clickable="true"
          data-acsb-navigable="true"
          data-acsb-now-navigable="true"
          data-acsb-menu="a"
          data-acsb-menu-root-link="true"
          data-acsb-textual-ops="contact"
          data-acsb-hover="true"
          onClick={() => trackEventDirect('Top Navigation', 'Click', 'Contact Us')}
        >
          <i
            className="iconfont iconfont-chat"
            aria-hidden="true"
            data-acsb-hidden="true"
            data-acsb-force-hidden="true"
          />
          <span>Contact Us</span>
        </a>
      </li>
      <li className="has-ico" data-acsb-menu="li" data-acsb-menu-root="true">
        {isAuthenticated ? (
          <a
            id="user_authenticated_xs"
            href="/accounts/"
            aria-label="My account"
            data-event1-category="Top Navigation"
            data-event1-action="Click"
            data-event1-label="My Account"
            data-noninteract="false"
            data-acsb-overflower="true"
            data-acsb-clickable="true"
            data-acsb-navigable="true"
            data-acsb-now-navigable="true"
            data-acsb-menu="a"
            data-acsb-menu-root-link="true"
            data-acsb-textual-ops="account"
            data-acsb-hover="true"
            onClick={() => trackEventDirect('Top Navigation', 'Click', 'My Account')}
          >
            <i
              className="iconfont iconfont-user"
              aria-hidden="true"
              data-acsb-hidden="true"
              data-acsb-force-hidden="true"
            />
            <span>My Account</span>
          </a>
        ) : (
          <a
            aria-label="Sign In"
            data-acsb-clickable="true"
            data-acsb-navigable="true"
            data-acsb-now-navigable="false"
            data-acsb-hidden="true"
            data-acsb-textual-ops="account"
            data-acsb-hover="true"
            href="#"
            id="user_unauthenticated_xs"
            onClick={onSignInClick}
            tabIndex={-1}
          >
            <i
              className="iconfont iconfont-user"
              aria-hidden="true"
              data-acsb-hidden="true"
              data-acsb-force-hidden="true"
            />
            <span>Sign In</span>
          </a>
        )}
      </li>
      <li className="has-ico" data-acsb-menu="li" data-acsb-menu-root="true">
        <a
          href="/shop/wishlist/"
          id="wishlist-toggler"
          aria-label="Wish List"
          data-event1-category="Top Navigation"
          data-event1-action="Click"
          data-event1-label="Wish List"
          data-noninteract="false"
          data-acsb-overflower="true"
          data-acsb-clickable="true"
          data-acsb-navigable="true"
          data-acsb-now-navigable="true"
          data-acsb-menu="a"
          data-acsb-menu-root-link="true"
          data-acsb-textual-ops="favorites"
          data-acsb-hover="true"
          onClick={() => trackEventDirect('Top Navigation', 'Click', 'Wish List')}
        >
          <i
            className="iconfont iconfont-heart"
            aria-hidden="true"
            data-acsb-hidden="true"
            data-acsb-force-hidden="true"
          />
          Wish List
          <span id="mobile_wishlist" className={classNames({ hidden: !wishlist?.items?.length })}>
            (
            <span className="text-purple" data-container="wishlist-count">
              {wishlist?.items?.length}
            </span>
            )
          </span>
        </a>
      </li>
      <li className="has-ico" data-acsb-menu="li" data-acsb-menu-root="true">
        <a
          data-event1-label="My Bag"
          data-event1-action="Click"
          data-event1-category="Top Navigation"
          data-noninteract="false"
          href="/shop/cart/"
          aria-label="My Shopping Bag"
          data-acsb-overflower="true"
          data-acsb-clickable="true"
          data-acsb-navigable="true"
          data-acsb-now-navigable="true"
          data-acsb-menu="a"
          data-acsb-menu-root-link="true"
          data-acsb-textual-ops="close"
          data-acsb-hover="true"
          onClick={() => trackEventDirect('Top Navigation', 'Click', 'My Bag')}
        >
          <i
            className="iconfont iconfont-bag"
            aria-hidden="true"
            data-acsb-hidden="true"
            data-acsb-force-hidden="true"
          />
          My Bag{' '}
          <span className={classNames('mybag-count-mobile', { hidden: !cart?.items?.length })}>
            (<span className="text-purple king-mybag-count-mobile">{cart?.totalItems}</span>)
          </span>
        </a>
      </li>
      <li className="has-ico" data-acsb-menu="li" data-acsb-menu-root="true">
        <a
          data-event1-label="Search"
          data-event1-action="Click"
          data-event1-category="Top Navigation"
          data-noninteract="false"
          href="/search/"
          aria-label="Search"
          data-acsb-overflower="true"
          data-acsb-clickable="true"
          data-acsb-navigable="true"
          data-acsb-now-navigable="true"
          data-acsb-menu="a"
          data-acsb-menu-root-link="true"
          data-acsb-textual-ops="search"
          data-acsb-hover="true"
          onClick={() => trackEventDirect('Top Navigation', 'Click', 'Search')}
        >
          <i
            className="iconfont iconfont-search"
            aria-hidden="true"
            data-acsb-hidden="true"
            data-acsb-force-hidden="true"
          />
          Search
        </a>
      </li>
      {setCountryElement()}
    </>
  );
}

function SubMenuItem({ index, currentIndex, setCurrentIndex, itemMenu, children }) {
  const featuredEngagementRing = itemMenu.label === 'ENGAGEMENT RINGS';

  const currentCurrencyCode = useStore(state => state.user?.currSymbol) || '$';
  const isGlobalEFlag = useStore(state => state.user?.globalEFlag) || false;
  const isGlobalESite = useStore(state => state.user?.globalESite) || false;

  return (
    <div className={classNames('mm-panel mm-hasheader', { 'mm-opened': currentIndex === index })}>
      <div className="mm-header">
        <a
          onClick={() => setCurrentIndex(null)}
          className="mm-btn mm-prev"
          role="button"
          tabIndex={0}
          onKeyDown={event => {
            if (event.key === 'Enter') {
              setCurrentIndex(null);
            }
          }}
        />
      </div>
      <div className="mm-body">
        {itemMenu.headers &&
          itemMenu.headers.map((header: MobileNavLinkHeaderProp, headerNumber: number) => (
            <dl
              key={headerNumber}
              className={
                header.isCategoryBg && !(featuredEngagementRing && header.title?.label === 'FEATURED')
                  ? 'categorybg'
                  : ''
              }
            >
              <dt className="mm-header-subsection_title">
                {header?.title?.url ? (
                  <a
                    href={header?.title?.url}
                    onClick={e =>
                      handleGaClick(e, 'top_navigation_click', header?.title?.url, header.title?.label, 'top nav')
                    }
                    {...getDataAttributes(header)}
                  >
                    {header.title?.label.toUpperCase()}
                  </a>
                ) : (
                  header.title?.label.toUpperCase()
                )}
              </dt>
              {header.items &&
                header.items.map((headerItem: MobileHeaderItem, headerItemIndex: number) => (
                  <dd key={headerItemIndex} className={headerItem.itemClass}>
                    <a
                      href={headerItem?.url}
                      onClick={e =>
                        handleGaClick(e, 'top_navigation_click', headerItem?.url, headerItem?.label, 'top nav')
                      }
                      {...getDataAttributes(headerItem)}
                    >
                      {headerItem.icon && <i className={headerItem.icon} />}
                      {(isGlobalEFlag && !isGlobalESite) || !isGlobalEFlag
                        ? headerItem?.label.replace(/\$/g, currentCurrencyCode)
                        : headerItem?.label}
                    </a>
                  </dd>
                ))}
            </dl>
          ))}
        <div className="submenu-hasicon-groups">
          <dl>
            <dd>
              <ul className="mm-listview">{children}</ul>
            </dd>
          </dl>
        </div>
      </div>
    </div>
  );
}

/** Contents of `li.main-nav-search`, split out so the static island can portal it into its slot. */
export function SearchPanel({ isOpen }: { isOpen: boolean }) {
  return (
    <dl className="mb0">
      <dd>
        <div className="king-navgation-mobile">
          <div className="home-search-mobile-wrap" id="search-panel-mobile">
            <div className="wrapper">
              <div className="tw-flex tw-relative">
                <div
                  className={classNames('tw-w-full', {
                    'king-search': isOpen,
                  })}
                  role="search"
                >
                  <form
                    id={isOpen ? 'king-search-form' : 'navigation-form'}
                    action="/search/"
                    method="get"
                    className="input-group input-group-sm home-search-mobile"
                    data-acsb-form-uuid="a9tg4uqsga1s"
                    data-acsb-form-type="search"
                  >
                    <input
                      id={isOpen ? 'king-search-input' : 'navigation-input'}
                      type="text"
                      data-category="Submit Search"
                      className="inner-search-input form-control placeholder searchspring-query tw-font-book"
                      autoComplete="off"
                      name="q"
                      placeholder="Search"
                      aria-label="Search"
                      spellCheck="false"
                      data-acsb-navigable="true"
                      data-acsb-now-navigable="true"
                      aria-hidden="false"
                      data-acsb-hidden="false"
                      data-acsb-validation-uuid="ai3l2cc15e"
                      data-acsb-field-visible="true"
                      aria-invalid="false"
                      aria-required="false"
                      data-acsb-tooltip="Search"
                    />
                    <div className="ss-targeted" aria-hidden="true" data-acsb-hidden="true">
                      <div className="ss-ac-wrapper">
                        <div id="ss-ac-terms">
                          <ul className="mm-listview" data-acsb-menu="ul" role="presentation" />
                        </div>
                        <div id="ss-ac-results">
                          <ul className="item-results mm-listview" data-acsb-menu="ul" role="presentation" />
                        </div>
                      </div>
                    </div>
                    <span className="input-group-btn">
                      <button
                        type="button"
                        onClick={e => trackEvent(e)}
                        data-event1-category="Submit Search"
                        data-event1-action="undefined"
                        className="btn btn-default iconfont iconfont-search"
                        data-acsb-clickable="true"
                        data-acsb-navigable="true"
                        data-acsb-now-navigable="true"
                        data-acsb-textual-ops="search"
                      >
                        <span
                          className="acsb-sr-only"
                          data-acsb-sr-only="true"
                          data-acsb-force-visible="true"
                          aria-hidden="false"
                          data-acsb-hidden="false"
                        >
                          Submit Search
                        </span>
                      </button>
                    </span>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </dd>
    </dl>
  );
}

/**
 * Companion to the static drawer island rendered by `renderMobileNavShellHtml`. The panels
 * themselves are plain HTML that React never reconciles — so Dynamic Yield's pre-hydration
 * edits survive — and this component only fills the island's slots with the store-driven
 * chrome (search form, Sign In / Wish List / Bag / country) via portals.
 */
export function MobileNavigationIsland({ isOpen, rootId }: { isOpen: boolean; rootId: string }) {
  const [slots, setSlots] = useState<{
    search: Element | null;
    rootList: Element | null;
    panels: Element[];
  }>({ search: null, rootList: null, panels: [] });
  const [currentIndex, setCurrentIndex] = useState<number | null>(null);
  const isMobile = useMediaQuery('(max-width: 767px)');
  const currencyCode = useStore(state => state.user?.currSymbol) || '$';
  const isGlobalEFlag = useStore(state => state.user?.globalEFlag) || false;
  const isGlobalESite = useStore(state => state.user?.globalESite) || false;

  useEffect(() => {
    const root = document.getElementById(rootId);
    if (!root) return;

    setSlots({
      search: root.querySelector('[data-be-nav-search-slot]'),
      rootList: root.querySelector('[data-be-nav-root-list]'),
      panels: Array.from(root.querySelectorAll('[data-be-nav-chrome-slot]')),
    });
  }, [rootId]);

  useMobileNavBehavior({
    rootId,
    enabled: true,
    currencyCode,
    isGlobalEFlag,
    isGlobalESite,
    onPanelChange: setCurrentIndex,
  });

  return (
    <>
      {slots.search && createPortal(<SearchPanel isOpen={isOpen} />, slots.search)}
      {slots.rootList &&
        createPortal(<MainMenu injectGlbeCountry={isMobile && currentIndex === null} />, slots.rootList)}
      {slots.panels.map((slot, index) =>
        createPortal(<MainMenu injectGlbeCountry={isMobile && currentIndex === index} />, slot, `panel-${index}`)
      )}
    </>
  );
}

export default function MobileNavigation({ isOpen, mobileNavLinks = [] }: MobileNavigationProps) {
  const storeMobileLinks: MobileNavLinksProps[] = useStore(state => state.mobileNavLinks);
  const [currentIndex, setCurrentIndex] = useState<number | null>(null);
  const isMobile = useMediaQuery('(max-width: 767px)');
  const mobileLinks = storeMobileLinks?.length ? storeMobileLinks : (mobileNavLinks as MobileNavLinksProps[]);

  const onMobileNavClick = (event: any, mobileLink: MobileNavLinksProps, index: number) => {
    if (mobileLink.itemMenu?.url) return;
    event.preventDefault();
    setCurrentIndex(index);
  };

  return (
    <>
      <div className={classNames('mm-panel mm-opened', { 'mm-subopened': currentIndex !== null })}>
        <ul className="mm-listview mm-first mm-last">
          <li className="main-nav-search">
            <SearchPanel isOpen={isOpen} />
          </li>
          {mobileLinks &&
            mobileLinks.length > 0 &&
            mobileLinks.map((mobileLink: MobileNavLinksProps, index: number) => {
              const isShowArrow = mobileLink.itemMenu?.headers?.length;
              return (
                <li key={index}>
                  {isShowArrow && (
                    <a
                      className="mm-next"
                      href={mobileLink.itemMenu.url || '#'}
                      onClick={e => onMobileNavClick(e, mobileLink, index)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={e => onMobileNavClick(e, mobileLink, index)}
                    >
                      <span className="sr-only">Drop Down</span>
                    </a>
                  )}
                  <a
                    data-top-label={mobileLink.itemMenu.label}
                    className="tw-normal-case"
                    href={mobileLink.itemMenu.url || '#'}
                    onClick={e => {
                      onMobileNavClick(e, mobileLink, index);
                      handleGaClick(e, 'top_navigation_click', '', mobileLink.itemMenu.label, 'top nav');
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={event => {
                      if (event.key === 'Enter') {
                        onMobileNavClick(event, mobileLink, index);
                      }
                    }}
                    {...getDataAttributes(mobileLink.itemMenu)}
                  >
                    {mobileLink.itemMenu.label}
                  </a>
                </li>
              );
            })}
          <MainMenu injectGlbeCountry={isMobile && currentIndex === null} />
        </ul>
      </div>
      {mobileLinks &&
        mobileLinks.length > 0 &&
        mobileLinks.map(({ itemMenu }: MobileNavLinksProps, index: number) => (
          <SubMenuItem
            index={index}
            key={index}
            currentIndex={currentIndex}
            setCurrentIndex={setCurrentIndex}
            itemMenu={itemMenu}
          >
            <MainMenu injectGlbeCountry={isMobile && currentIndex === index} />
          </SubMenuItem>
        ))}
    </>
  );
}
